import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { ReactNode, useEffect, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

import ClientOnly from "@/components/ClientOnly"
import { IS_PROD } from "@/lib/constants"
import { url } from "@/lib/url"
import type { AdminBarProps } from "@/v2/components/AdminBar"
import { ErrorState } from "@/v2/components/ErrorState"
import { IdleTimerProps } from "@/v2/components/IdleTimer"
import FullStory from "@/v2/components/analytics/FullStory"
import { useCurrentUserQuery } from "@/v2/graphql/public-generated-rq"
import { usePropertyPid } from "@/v2/hooks/router"
import { AuthRequired, PublicAuthProvider, useAuth } from "@/v2/providers/Auth"
import { useFlags } from "@/v2/providers/FeatureFlags"
import { twMerge } from "@lumen-energy/design-system"

import { CommandBar } from "../../CommandBar"
import { HubspotScript } from "../../HubspotScript"
import { Header } from "./Header"
import { Sidebar } from "./Sidebar"

const AdminBar = dynamic<AdminBarProps>(
  () => import("@/v2/components/AdminBar").then((mod) => mod.AdminBar),
  { ssr: false },
)

const IdleTimer = dynamic<IdleTimerProps>(
  () => import("@/v2/components/IdleTimer").then((mod) => mod.IdleTimer),
  { ssr: false },
)

export interface AppLayoutProps {
  children: React.ReactNode
  focused?: boolean
}

export const AppLayout = (props: AppLayoutProps) => {
  return (
    <PublicAuthProvider>
      <AuthRequired>
        <PerhapsRedirectToMultiFinancingUX>
          <Layout {...props} />
        </PerhapsRedirectToMultiFinancingUX>
      </AuthRequired>
    </PublicAuthProvider>
  )
}

function isDataCollectionPathname(pathname: string) {
  return (
    pathname.startsWith("/data-collection") ||
    pathname === "/o/portfolio/[portfolioPid]/data-collection" ||
    pathname === "/s/portfolio/[portfolioPid]/data-collection"
  )
}

function PerhapsRedirectToMultiFinancingUX({
  children,
}: {
  children: React.ReactNode
}) {
  const flags = useFlags()
  const { pathname, replace } = useRouter()
  const { isAdmin } = useAuth()

  const { data } = useCurrentUserQuery()
  const orgSlug = data?.currentUser?.org?.slug
  const propertyPid = usePropertyPid()

  const shouldRedirectToRevampUX =
    !isAdmin &&
    flags.General.Revamp &&
    orgSlug &&
    !flags.General.RevampHoldoutOrgSlugs.includes(orgSlug) &&
    // We have explicitly handled redirects for data collection pathnames, so don't redirect to the
    // Revamp UX via this mechanism.
    !isDataCollectionPathname(pathname)

  useEffect(() => {
    if (shouldRedirectToRevampUX) {
      if (propertyPid) {
        replace(url(["/[orgSlug]/properties/[propertyPid]", { orgSlug, propertyPid }]))
      } else {
        // If there is no propertyPid, redirect to the org root.
        replace(url(["/[orgSlug]", { orgSlug }]))
      }
    }
  }, [orgSlug, propertyPid, replace, shouldRedirectToRevampUX])

  // We optimistically load the children before finding out if we need to redirect for better
  // performance.
  if (shouldRedirectToRevampUX) {
    return null
  } else {
    return <>{children}</>
  }
}

const Layout = ({ children, focused }: AppLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isAdmin } = useAuth()

  const router = useRouter()

  const { data } = useCurrentUserQuery()
  const timeout = data?.currentUser.org?.inactivityTimeoutMs

  useEffect(() => {
    const closeSidebar = () => setSidebarOpen(false)
    router.events.on("routeChangeComplete", closeSidebar)
    return () => {
      router.events.off("routeChangeComplete", closeSidebar)
    }
  }, [router.events])

  return (
    <ClientOnly>
      <HubspotScript />
      {IS_PROD && isAdmin === false ? <FullStory /> : null}
      {isAdmin ? <AdminBar /> : null}
      {isAdmin ? <CommandBar /> : null}
      {!!timeout && <IdleTimer timeout={timeout} />}
      <div className="min-h-screen w-full bg-grey-100">
        {!focused && <Header onToggleSidebar={() => setSidebarOpen((v) => !v)} />}
        <div className="flex">
          {!focused && (
            <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          )}
          <main className="min-h-full w-full @container/page">
            <ErrorBoundary
              fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorState
                  title="Something went wrong"
                  subTitle="Try again or contact support@lumen.energy"
                  retryLabel="Try again"
                  onRetry={resetErrorBoundary}
                  rawError={error}
                />
              )}
            >
              {children}
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </ClientOnly>
  )
}

export const PageContainer = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <div className={twMerge("mx-auto h-full w-full max-w-screen-2xl", className)}>
      {children}
    </div>
  )
}
